import Gravatar from 'react-gravatar';
import { Transition, Menu } from '@headlessui/react';
import Link from 'next/link';
import React, { useRef } from 'react';
import { useTheme } from 'next-themes';
import { useAuth } from '../auth';
import Sun from '../icons/sun';
import Moon from '../icons/moon';
import Monitor from '../icons/monitor';
import { cn } from './cn';
import { dropdownItemClasses, dropdownItemActiveClasses } from './recipes/dropdown';

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
] as const;

const ThemeSwitch = () => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const active = mounted ? theme : 'system';

  return (
    <div className="px-2 py-2.5 flex items-center justify-between gap-3">
      <span className="text-[13px] font-medium text-neutral-700">Theme</span>
      <div
        className="inline-flex items-center rounded-control border border-subtle bg-neutral-50 p-0.5"
        role="group"
        aria-label="Color theme"
      >
        {THEME_OPTIONS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            aria-label={label}
            aria-pressed={active === value}
            // Prevent Headless UI Menu from closing on mousedown focus shift
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTheme(value);
            }}
            className={cn(
              'inline-flex h-7 w-7 items-center justify-center rounded-[5px] text-neutral-500 transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/30',
              active === value
                ? 'bg-surface text-neutral-900 shadow-xs'
                : 'hover:text-neutral-700'
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        ))}
      </div>
    </div>
  );
};

const Avatar = () => {
  const { user, loading, logout } = useAuth();
  const menu = useRef<HTMLButtonElement>(null);
  if (loading || !user) {
    return null;
  }
  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button
            id="account-menu"
            ref={menu}
            className="max-w-xs flex items-center text-sm rounded-full shadow-xs focus:outline-none focus:ring-2 focus:ring-blue/30 focus:ring-offset-1"
          >
            <span className="sr-only">Open user menu</span>
            {user?.picture ? (
              <img src={user?.picture} alt="avatar" className="h-9 w-9 rounded-full avatar" />
            ) : (
              <Gravatar
                email={user?.attributes?.email || user?.email}
                className="h-9 w-9 rounded-full avatar"
              />
            )}
          </Menu.Button>

          <Transition
            show={open}
            enter="transition ease-out-strong duration-150"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-out-strong duration-100"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              static
              className="dropdown-menu absolute right-0 z-50 w-56 mt-2 origin-top-right divide-y divide-subtle"
            >
              <div className="px-2 py-3">
                <p className="text-[13px] leading-5 text-neutral-500">Signed in as</p>
                <p className="text-[13px] font-medium leading-5 text-neutral-900 truncate">
                  {user?.attributes?.email || user?.email}
                </p>
              </div>

              <div className="py-0.5">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      href="/account"
                      onClick={() => menu.current?.click()}
                      className={cn(
                        dropdownItemClasses,
                        active && dropdownItemActiveClasses
                      )}
                    >
                      Account settings
                    </Link>
                  )}
                </Menu.Item>
              </div>

              <ThemeSwitch />

              <div className="py-0.5">
                <Menu.Item>
                  {({ active }) => (
                    <a
                      href="#sign-out"
                      onClick={(e) => {
                        e.preventDefault();
                        logout();
                      }}
                      className={cn(
                        dropdownItemClasses,
                        active && dropdownItemActiveClasses
                      )}
                    >
                      Sign out
                    </a>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default Avatar;
